<script lang="ts">
  import type {
    DrawEntry,
    Participant,
    PipelineState,
    PipelineStep,
  } from '@pin/schemas/admin';
  import { Subject } from 'rxjs';
  import { untrack } from 'svelte';
  import { cubicOut } from 'svelte/easing';
  import { SvelteMap } from 'svelte/reactivity';
  import { fade, slide } from 'svelte/transition';
  import { drawCandidates, getExistingDraw, setWinner } from '../../api/draw';
  import { startPipeline } from '../../api/pipeline';
  import { createPipelinePoller } from '../../api/pipelinePoller';
  import NameInputStep from './NameInputStep.svelte';

  type StepStatus = 'pending' | 'active' | 'running' | 'done' | 'error';

  interface CorrectGroupInfo {
    name: string;
    count: number;
    percentage: number;
    variantCount: number;
  }

  interface Props {
    participant: Participant;
    correctGroups: CorrectGroupInfo[];
    onComplete: () => void;
    onCancel: () => void;
  }

  const { participant, correctGroups, onComplete, onCancel }: Props = $props();

  const STEPS = [
    { id: 1, title: 'Skriv inn virkelig navn' },
    { id: 2, title: 'Trekker vinnere' },
    { id: 3, title: 'Godkjenn vinner' },
    { id: 4, title: 'Bekreft' },
    { id: 5, title: 'Fullfører' },
  ] as const;

  const PIPELINE_STEPS = [
    { key: 'store_stats', label: 'Lagrer statistikk' },
    { key: 'draw_winner', label: 'Bekrefter vinner' },
    { key: 'unmask', label: 'Avmaskerer' },
    { key: 'reorder', label: 'Sorterer deltakere' },
    { key: 'guess_distribution', label: 'Beregner gjettfordeling' },
    { key: 'unlock', label: 'Åpner runden' },
  ] as const;

  /* Svelte transitions take plain milliseconds, so reduced motion is handled
     here instead of in CSS. */
  const motion = (ms: number): number =>
    matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : ms;

  const getInitialName = (): string => {
    try {
      const stored = localStorage.getItem(`unmask-name-${participant.id}`);
      if (stored) return stored;
    } catch {
      // ignore
    }
    return correctGroups.at(0)?.name ?? '';
  };

  let currentStep = $state(1);
  const stepStatuses = new SvelteMap<number, StepStatus>([
    [1, 'active'],
    [2, 'pending'],
    [3, 'pending'],
    [4, 'pending'],
    [5, 'pending'],
  ]);
  let error = $state<string | null>(null);
  let isLoading = $state(true);

  let unmaskedName = $state(getInitialName());
  let winnerEntry = $state<DrawEntry | null>(null);
  let allCandidates = $state<DrawEntry[]>([]);
  let showingAlternatives = $state(false);
  let drawId = $state<string | null>(null);

  let pipelineState = $state<PipelineState | null>(null);
  const stopPolling$ = new Subject<void>();

  let dialog = $state<HTMLDialogElement>();
  let closedByUs = false;

  /* Once the pipeline has started the participant may already be unmasked,
     so the wizard must stay open until the user has seen the receipt. */
  const pipelineStarted = $derived(
    (stepStatuses.get(5) ?? 'pending') !== 'pending'
  );
  /* Shown behind the step title once the step is done, so the answer stays
     visible after the step collapses. */
  const stepDetails = $derived.by(() => {
    const winnerName = winnerEntry
      ? `${winnerEntry.firstName} ${winnerEntry.lastName ?? ''}`.trim()
      : '';
    return new Map<number, { text: string; hint: string }>([
      [
        1,
        {
          text: stepStatuses.get(1) === 'done' ? unmaskedName.trim() : '',
          hint: 'Navnet seerne ser når masken tas av',
        },
      ],
      [
        3,
        {
          text: stepStatuses.get(3) === 'done' ? winnerName : '',
          hint: 'Godkjent vinner',
        },
      ],
    ]);
  });
  const pipelineRunning = $derived(stepStatuses.get(5) === 'running');
  const anyStepRunning = $derived(
    [...stepStatuses.values()].some((s) => s === 'running')
  );

  const checkExistingDraw = async () => {
    isLoading = true;
    try {
      const existing = await getExistingDraw(participant.id);
      if (existing && existing.entries.length > 0) {
        allCandidates = existing.entries;
        const winner = existing.entries.find((e) => e.status === 'winner');
        if (winner) {
          winnerEntry = winner;
          drawId = winner.drawId;
          setStatus(1, 'done');
          setStatus(2, 'done');
          setStatus(3, 'active');
          currentStep = 3;
        }
      }
    } catch (e) {
      console.error('Failed to check existing draw:', e);
    }
    isLoading = false;
  };

  $effect(() => {
    const el = dialog;
    if (!el) return;
    // Run once on mount: the participant prop changes while the pipeline runs
    // and must not reset the wizard.
    untrack(() => {
      if (!el.open) el.showModal();
      void checkExistingDraw();
    });
  });

  const setStatus = (step: number, status: StepStatus) => {
    stepStatuses.set(step, status);
  };

  const getStatusIcon = (step: number): string => {
    const status = stepStatuses.get(step) ?? 'pending';
    switch (status) {
      case 'pending':
        return '○';
      case 'active':
        return '●';
      case 'running':
        return '↻';
      case 'done':
        return '✓';
      case 'error':
        return '✕';
    }
  };

  const isStepClickable = (step: number): boolean => {
    if (step === 2 || step === 5 || step === currentStep) return false;
    if (pipelineStarted || anyStepRunning) return false;
    const status = stepStatuses.get(step) ?? 'pending';
    return status === 'done' || status === 'active';
  };

  const goToStep = (step: number) => {
    if (!isStepClickable(step)) return;
    error = null;
    showingAlternatives = false;
    currentStep = step;
  };

  const handleStep1Submit = async (name: string) => {
    unmaskedName = name;
    setStatus(1, 'done');
    // Coming back to edit the name must not draw new winners.
    if (stepStatuses.get(2) === 'done') {
      currentStep = stepStatuses.get(3) === 'done' ? 4 : 3;
      if (stepStatuses.get(currentStep) === 'pending') {
        setStatus(currentStep, 'active');
      }
      return;
    }
    currentStep = 2;
    setStatus(2, 'running');
    await runStep2();
  };

  const runStep2 = async () => {
    error = null;
    setStatus(2, 'running');
    try {
      const result = await drawCandidates(participant.id);
      allCandidates = result.entries;
      const winner = result.entries.find((e) => e.status === 'winner');
      if (winner) {
        winnerEntry = winner;
        drawId = winner.drawId;
      } else {
        winnerEntry = null;
        drawId = null;
      }
      setStatus(2, 'done');
      currentStep = 3;
      setStatus(3, 'active');
    } catch (e) {
      console.error(e);
      error = e instanceof Error ? e.message : 'Trekning feilet.';
      setStatus(2, 'error');
    }
  };

  const handleApproveWinner = async () => {
    error = null;
    setStatus(3, 'running');
    try {
      if (drawId && winnerEntry) {
        await setWinner(drawId, winnerEntry.userId);
      }
      setStatus(3, 'done');
      currentStep = 4;
      setStatus(4, 'active');
    } catch (e) {
      console.error(e);
      error = e instanceof Error ? e.message : 'Kunne ikke sette vinner.';
      setStatus(3, 'error');
    }
  };

  const handleShowAlternatives = () => {
    showingAlternatives = true;
  };

  const handleSelectAlternative = (candidate: DrawEntry) => {
    winnerEntry = candidate;
    showingAlternatives = false;
    // A changed winner must be approved (saved) again before confirming.
    setStatus(3, 'active');
    setStatus(4, 'pending');
  };

  const otherCandidates = $derived(
    allCandidates.filter((c) => c.userId !== winnerEntry?.userId)
  );

  const handleProceed = async () => {
    setStatus(4, 'done');
    currentStep = 5;
    setStatus(5, 'running');
    await runStep5();
  };

  const runStep5 = async () => {
    error = null;
    setStatus(5, 'running');
    pipelineState = null;

    try {
      const result = await startPipeline(participant.id, unmaskedName.trim());
      if (!result.started) {
        throw new Error(result.error);
      }

      createPipelinePoller(participant.id, stopPolling$).subscribe({
        next: (state) => {
          pipelineState = state;
          if (state.status === 'completed') {
            setStatus(5, 'done');
          } else if (state.status === 'failed') {
            error = state.error ?? 'Pipeline feilet.';
            setStatus(5, 'error');
          }
        },
        error: (e) => {
          console.error('Pipeline polling error:', e);
          error = 'Polling feilet.';
          setStatus(5, 'error');
        },
      });
    } catch (e) {
      console.error(e);
      error = e instanceof Error ? e.message : 'Kunne ikke starte pipeline.';
      setStatus(5, 'error');
    }
  };

  const stopPolling = () => {
    stopPolling$.next();
  };

  const handleRetry = () => {
    if (currentStep === 2) {
      void runStep2();
    } else if (currentStep === 3) {
      void handleApproveWinner();
    } else if (currentStep === 5) {
      void runStep5();
    }
  };

  const handleClose = () => {
    if (pipelineRunning) return;
    if (pipelineStarted) {
      handleDone();
      return;
    }
    stopPolling();
    closedByUs = true;
    dialog?.close();
    onCancel();
  };

  const handleDone = () => {
    stopPolling();
    closedByUs = true;
    dialog?.close();
    onComplete();
  };

  /* The browser can close a modal on its own (e.g. repeated Escape). Keep the
     wizard visible after the pipeline has started so the receipt is shown. */
  const handleNativeClose = () => {
    if (closedByUs) return;
    if (pipelineStarted) {
      dialog?.showModal();
    } else {
      stopPolling();
      onCancel();
    }
  };

  const getPipelineStepStatus = (
    stepKey: PipelineStep
  ): 'pending' | 'running' | 'done' => {
    if (!pipelineState) return 'pending';
    const steps = PIPELINE_STEPS.map((s) => s.key);
    const currentIndex = pipelineState.currentStep
      ? steps.indexOf(pipelineState.currentStep)
      : -1;
    const stepIndex = steps.indexOf(stepKey);

    if (pipelineState.status === 'completed') return 'done';
    if (stepIndex < currentIndex) return 'done';
    if (stepIndex === currentIndex) return 'running';
    return 'pending';
  };
</script>

<dialog
  bind:this={dialog}
  oncancel={(e) => {
    e.preventDefault();
    if (!pipelineStarted) handleClose();
  }}
  onclose={handleNativeClose}
>
  <div class="wizard">
    <header class="wizard-header">
      <h2 class="wizard-title">Avmasker {participant.maskName}</h2>
      <button
        class="close-btn"
        onclick={handleClose}
        disabled={pipelineRunning}
        aria-label="Lukk">✕</button
      >
    </header>

    <div class="correct-groups" class:no-correct={correctGroups.length === 0}>
      {#if correctGroups.length === 0}
        <span class="correct-label">Ingen riktige svar</span>
        <p class="no-correct-text">Ingen gjettet riktig denne gangen.</p>
      {:else}
        <span class="correct-label"
          >Grupper godkjent som riktige svar (gir rett gjett):</span
        >
        <ul class="correct-list">
          {#each correctGroups as group (group.name)}
            <li class="correct-item">
              <span class="correct-name">{group.name}</span>
              <span class="correct-stats">
                {group.count.toLocaleString('nb')} gjett ({group.percentage.toFixed(
                  1
                )} %), {group.variantCount} varianter
              </span>
            </li>
          {/each}
        </ul>
      {/if}
    </div>

    <div class="stepper">
      {#if isLoading}
        <p class="loading-text">Laster …</p>
      {:else}
        {#each STEPS as step (step.id)}
          {@const status = stepStatuses.get(step.id) ?? 'pending'}
          <div
            class="step"
            class:active={step.id === currentStep}
            class:done={status === 'done'}
            class:error={status === 'error'}
          >
            <div class="step-indicator" class:running={status === 'running'}>
              {getStatusIcon(step.id)}
            </div>
            <div class="step-content">
              <h3 class="step-title">
                {#if isStepClickable(step.id)}
                  <button
                    type="button"
                    class="step-link"
                    onclick={() => goToStep(step.id)}
                  >
                    {step.title}
                  </button>
                {:else}
                  {step.title}
                {/if}
                {#if stepDetails.get(step.id)?.text}
                  {@const detail = stepDetails.get(step.id)}
                  <span class="step-detail" title={detail?.hint}
                    >({detail?.text})</span
                  >
                {/if}
              </h3>

              {#if step.id === currentStep}
                <div
                  class="step-body"
                  transition:slide={{ duration: motion(250), easing: cubicOut }}
                >
                  <!-- Re-keyed per sub-state so e.g. "Trekker vinnere" → winner
                       card fades in instead of swapping in a single frame. -->
                  {#key `${status}-${showingAlternatives}`}
                    <div class="step-pane" in:fade={{ duration: motion(200) }}>
                      {#if step.id === 1}
                        <NameInputStep
                          participantId={participant.id}
                          maskName={participant.maskName}
                          initialName={unmaskedName}
                          onSubmit={handleStep1Submit}
                        />
                      {:else if step.id === 2}
                        {#if status === 'running'}
                          <p class="status-text">Trekker vinnere …</p>
                        {:else if status === 'error'}
                          <p class="error-text">{error}</p>
                          <button class="btn secondary" onclick={handleRetry}
                            >Prøv igjen</button
                          >
                        {/if}
                      {:else if step.id === 3}
                        {#if status === 'running'}
                          <p class="status-text">Lagrer vinner …</p>
                        {:else if status === 'error'}
                          <p class="error-text">{error}</p>
                          <button class="btn secondary" onclick={handleRetry}
                            >Prøv igjen</button
                          >
                        {:else if showingAlternatives}
                          <p class="alternatives-label">Velg ny vinner:</p>
                          <ul class="candidates-list">
                            {#each otherCandidates as candidate (candidate.userId)}
                              <li class="candidate-item">
                                <button
                                  type="button"
                                  class="candidate-btn"
                                  onclick={() =>
                                    handleSelectAlternative(candidate)}
                                >
                                  <span class="candidate-name">
                                    {candidate.firstName}
                                    {candidate.lastName ?? ''}
                                  </span>
                                  <span class="candidate-email"
                                    >{candidate.email}</span
                                  >
                                </button>
                              </li>
                            {/each}
                          </ul>
                          <button
                            class="btn secondary"
                            onclick={() => (showingAlternatives = false)}
                          >
                            Avbryt
                          </button>
                        {:else if winnerEntry}
                          <div class="winner-card">
                            <dl class="winner-details">
                              <div class="detail-row">
                                <dt>Navn</dt>
                                <dd>
                                  {winnerEntry.firstName}
                                  {winnerEntry.lastName ?? ''}
                                </dd>
                              </div>
                              <div class="detail-row">
                                <dt>E-post</dt>
                                <dd>{winnerEntry.email}</dd>
                              </div>
                              <div class="detail-row">
                                <dt>Type</dt>
                                <dd>
                                  {winnerEntry.identityType === 'Adult'
                                    ? 'Voksen'
                                    : 'Barn'}
                                </dd>
                              </div>
                              {#if winnerEntry.birthYear}
                                <div class="detail-row">
                                  <dt>Fødselsår</dt>
                                  <dd>{winnerEntry.birthYear}</dd>
                                </div>
                              {/if}
                            </dl>
                          </div>
                          <div class="step-actions">
                            <button
                              class="btn primary"
                              onclick={handleApproveWinner}
                            >
                              Godkjenn
                            </button>
                            {#if otherCandidates.length > 0}
                              <button
                                class="btn secondary"
                                onclick={handleShowAlternatives}
                              >
                                Velg annen
                              </button>
                            {/if}
                          </div>
                        {:else}
                          <div class="no-winner-notice">
                            <p class="no-winner-text">
                              Ingen gyldige vinnerkandidater ble funnet. Dette
                              kan skje hvis ingen har gjettet riktig, eller hvis
                              alle som gjettet riktig er ekskludert (ie.
                              testbrukere)
                            </p>
                          </div>
                          <button
                            class="btn primary"
                            onclick={handleApproveWinner}
                          >
                            Fortsett uten vinner
                          </button>
                        {/if}
                      {:else if step.id === 4}
                        <div class="receipt">
                          <p class="receipt-intro">Du er i ferd med å:</p>
                          <ul class="receipt-list">
                            <li>
                              Avmaskere <strong>{participant.maskName}</strong>
                              som
                              <strong>{unmaskedName.trim()}</strong>
                            </li>
                            {#if winnerEntry}
                              <li>
                                Sette vinner til <strong
                                  >{winnerEntry.firstName}
                                  {winnerEntry.lastName ?? ''}</strong
                                >
                              </li>
                            {:else}
                              <li class="no-winner">
                                Ingen vinner (ingen gyldige kandidater)
                              </li>
                            {/if}
                            <li>Sortere deltakere</li>
                            <li>Beregne statistikk</li>
                            <li>
                              Åpne runden for nye gjett / favorittmarkeringer
                            </li>
                          </ul>
                        </div>
                        <button class="btn primary" onclick={handleProceed}>
                          Fullfør avmasking
                        </button>
                      {:else if step.id === 5}
                        {#if status === 'running'}
                          <div class="pipeline-progress">
                            {#each PIPELINE_STEPS as pStep (pStep.key)}
                              {@const pStatus = getPipelineStepStatus(
                                pStep.key
                              )}
                              <div
                                class="pipeline-step"
                                class:done={pStatus === 'done'}
                                class:running={pStatus === 'running'}
                              >
                                <span class="pipeline-indicator">
                                  {pStatus === 'done'
                                    ? '✓'
                                    : pStatus === 'running'
                                      ? '↻'
                                      : '○'}
                                </span>
                                <span class="pipeline-label">{pStep.label}</span
                                >
                              </div>
                            {/each}
                          </div>
                        {:else if status === 'done'}
                          <div class="completion-receipt">
                            <div class="completion-icon">✓</div>
                            <p class="completion-text">
                              <strong>{participant.maskName}</strong> er
                              avmasket som
                              <strong>{unmaskedName.trim()}</strong>
                            </p>
                            {#if winnerEntry}
                              <p class="completion-winner">
                                Vinner: {winnerEntry.firstName}
                                {winnerEntry.lastName ?? ''} ({winnerEntry.email})
                              </p>
                            {:else}
                              <p class="completion-winner no-winner">
                                Ingen vinner trukket
                              </p>
                            {/if}
                            <p class="completion-note">
                              Runden er åpnet for nye gjett og
                              favorittmarkeringer
                            </p>
                          </div>
                          <button class="btn primary" onclick={handleDone}>
                            Lukk
                          </button>
                        {:else if status === 'error'}
                          <p class="error-text">{error}</p>
                          <button class="btn secondary" onclick={handleRetry}
                            >Prøv igjen</button
                          >
                        {/if}
                      {/if}
                    </div>
                  {/key}
                </div>
              {/if}
            </div>
          </div>
        {/each}
      {/if}
    </div>
  </div>
</dialog>

<style>
  dialog {
    box-sizing: border-box;
    padding: 0;
    width: 90%;
    max-width: 32rem;
    border-radius: var(--radius-lg);
    box-shadow: 0 10px 25px var(--shadow-md);
    background-color: var(--surface-raised);
    color: var(--text);
    border: 1px solid var(--border-strong);
    margin: auto;
  }

  dialog[open] {
    display: flex;
    flex-direction: column;
  }

  dialog::backdrop {
    background-color: var(--color-backdrop);
    backdrop-filter: blur(2px);
  }

  .wizard {
    display: flex;
    flex-direction: column;
  }

  .wizard-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: var(--space-4);
    border-block-end: var(--border-width) solid var(--border);
  }

  .wizard-title {
    margin: 0;
    font-size: var(--text-lg);
  }

  .close-btn {
    background: var(--surface-raised);
    border: 1px solid var(--border-subtle);
    color: var(--text-muted);
    width: 2rem;
    height: 2rem;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    padding: 0;

    &:hover:not(:disabled) {
      background: var(--border-subtle);
      color: var(--text);
    }

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
  }

  .correct-groups {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
    padding: var(--space-3) var(--space-4);
    background: light-dark(
      oklch(from var(--success) 95% calc(c * 0.3) h),
      oklch(from var(--success) 25% calc(c * 0.4) h)
    );
    border-block-end: var(--border-width) solid var(--border);

    &.no-correct {
      background: light-dark(
        oklch(from var(--warning) 95% calc(c * 0.3) h),
        oklch(from var(--warning) 25% calc(c * 0.4) h)
      );
    }
  }

  .correct-label {
    font-size: var(--text-sm);
    color: var(--text-muted);
  }

  .no-correct-text {
    margin: 0;
    font-size: var(--text-sm);
    color: var(--text);
  }

  .correct-list {
    margin: 0;
    padding: 0;
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
  }

  .correct-item {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: var(--space-2);
  }

  .correct-name {
    font-weight: var(--nrk-sans-weight-medium);
  }

  .correct-stats {
    font-size: var(--text-sm);
    color: var(--text-muted);
  }

  .stepper {
    display: flex;
    flex-direction: column;
    padding: var(--space-4);
    gap: var(--space-2);
  }

  .step {
    display: grid;
    grid-template-columns: 2rem 1fr;
    gap: var(--space-3);
    padding: var(--space-3);
    border-radius: var(--radius-md);
    background: var(--surface);

    &.active {
      background: var(--surface-raised);
      box-shadow: 0 1px 3px var(--shadow-sm);
    }

    &.done .step-indicator {
      color: var(--success);
    }

    &.error .step-indicator {
      color: var(--danger);
    }
  }

  .step-indicator {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: 2rem;
    font-size: var(--text-lg);
    color: var(--text-muted);

    &.running {
      animation: spin 1s linear infinite;
    }
  }

  /* A finished step pops its check so the move to the next step reads as
     progress rather than a jump. */
  .step.done .step-indicator {
    animation: step-done var(--duration-slow) var(--ease-in-out);
  }

  @keyframes step-done {
    0% {
      scale: 0.5;
      opacity: 0;
    }
    60% {
      scale: 1.25;
      opacity: 1;
    }
    100% {
      scale: 1;
    }
  }

  @keyframes spin {
    to {
      rotate: 1turn;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .step-indicator.running,
    .step.done .step-indicator {
      animation: none;
    }
  }

  .step-content {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }

  .step-title {
    margin: 0;
    font-size: var(--text-base);
    font-weight: var(--nrk-sans-weight-medium);
    line-height: 2rem;
  }

  .step-detail {
    margin-inline-start: var(--space-1);
    color: var(--text-muted);
    font-weight: var(--nrk-sans-weight-regular);
  }

  .step-link {
    padding: 0;
    border: 0;
    background: none;
    color: var(--accent);
    font: inherit;
    text-align: start;
    text-decoration: underline;
    text-underline-offset: 0.2em;
    cursor: pointer;

    &:hover {
      text-decoration-thickness: 2px;
    }

    &:focus-visible {
      outline: none;
      border-radius: var(--radius-sm);
      box-shadow: 0 0 0 var(--focus-ring-width) var(--accent);
    }
  }

  .step-pane {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }

  .step-body {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }

  .status-text,
  .loading-text {
    margin: 0;
    color: var(--text-muted);
    font-size: var(--text-sm);
  }

  .error-text {
    margin: 0;
    color: var(--danger);
    font-size: var(--text-sm);
  }

  .winner-card {
    padding: var(--space-3);
    background: var(--surface);
    border-radius: var(--radius-md);
    border: var(--border-width) solid var(--border);
  }

  .winner-details {
    margin: 0;
    display: grid;
    grid-template-columns: auto 1fr;
    gap: var(--space-2) var(--space-4);
  }

  .detail-row {
    display: contents;
  }

  .detail-row dt {
    color: var(--text-muted);
    font-size: var(--text-sm);
  }

  .detail-row dd {
    margin: 0;
  }

  .step-actions {
    display: flex;
    gap: var(--space-2);
  }

  .alternatives-label {
    margin: 0;
    font-size: var(--text-sm);
    color: var(--text-muted);
  }

  .candidates-list {
    margin: 0;
    padding: 0;
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }

  .candidate-item {
    display: contents;
  }

  .candidate-btn {
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
    padding: var(--space-3);
    background: var(--surface);
    border: var(--border-width) solid var(--border);
    border-radius: var(--radius-md);
    text-align: start;
    cursor: pointer;
    transition:
      background var(--duration-fast) var(--ease),
      border-color var(--duration-fast) var(--ease);

    &:hover {
      background: var(--surface-raised);
      border-color: var(--accent);
    }

    &:focus-visible {
      outline: none;
      box-shadow: 0 0 0 var(--focus-ring-width) var(--accent);
    }
  }

  .candidate-name {
    font-weight: var(--nrk-sans-weight-medium);
  }

  .candidate-email {
    font-size: var(--text-sm);
    color: var(--text-muted);
  }

  .receipt {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
    padding: var(--space-3);
    background: var(--surface);
    border-radius: var(--radius-md);
    border: var(--border-width) solid var(--border);
  }

  .receipt-intro {
    margin: 0;
    font-size: var(--text-sm);
    color: var(--text-muted);
  }

  .receipt-list {
    margin: 0;
    padding-inline-start: var(--space-4);
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
    font-size: var(--text-sm);
  }

  .receipt-list strong {
    font-weight: var(--nrk-sans-weight-medium);
  }

  .pipeline-progress {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }

  .pipeline-step {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    padding: var(--space-2);
    border-radius: var(--radius-sm);
    background: var(--surface);
    font-size: var(--text-sm);
    color: var(--text-muted);

    &.done {
      color: var(--success);
    }

    &.running {
      color: var(--text);
      background: var(--surface-raised);
    }
  }

  .pipeline-indicator {
    width: 1.25rem;
    text-align: center;
  }

  .pipeline-step.running .pipeline-indicator {
    animation: spin 1s linear infinite;
  }

  .pipeline-label {
    flex: 1;
  }

  .no-winner-notice {
    padding: var(--space-3);
    background: light-dark(
      oklch(from var(--warning) 95% calc(c * 0.3) h),
      oklch(from var(--warning) 25% calc(c * 0.4) h)
    );
    border-radius: var(--radius-md);
    border: var(--border-width) solid var(--warning);
  }

  .no-winner-text {
    margin: 0;
    font-size: var(--text-sm);
    color: var(--text);
  }

  .no-winner {
    color: var(--text-muted);
    font-style: italic;
  }

  .completion-receipt {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--space-2);
    padding: var(--space-4);
    background: light-dark(
      oklch(from var(--success) 95% calc(c * 0.3) h),
      oklch(from var(--success) 25% calc(c * 0.4) h)
    );
    border-radius: var(--radius-md);
    text-align: center;
  }

  .completion-icon {
    width: 3rem;
    height: 3rem;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: var(--text-xl);
    color: var(--success);
    background: var(--surface-raised);
    border-radius: 50%;
  }

  .completion-text {
    margin: 0;
    font-size: var(--text-base);
  }

  .completion-winner {
    margin: 0;
    font-size: var(--text-sm);
    color: var(--text-muted);
  }

  .completion-note {
    margin: var(--space-2) 0 0;
    padding-block-start: var(--space-2);
    border-block-start: var(--border-width) solid var(--border);
    font-size: var(--text-sm);
    color: var(--text);
  }
</style>
